<template>
  <section
    ref="panel"
    class="guided-audio-result-panel"
    :aria-label="$t('routes.guidedRecording.audioResult.statusHintAriaLabel')"
    :aria-labelledby="headingId"
    aria-atomic="true"
    aria-live="polite"
    role="status"
    tabindex="-1"
    data-cy="guidedRecording-audioResult"
  >
    <p class="guided-audio-result-panel__announcement">{{ announcement }}</p>
    <v-alert :type="alertType" variant="tonal" :icon="false">
      <div class="guided-audio-result-panel__heading">
        <v-icon aria-hidden="true">{{ statusIcon }}</v-icon>
        <h3 :id="headingId" class="text-h6">{{ statusText }}</h3>
      </div>
      <p class="mb-2">{{ guidance }}</p>
      <p class="text-body-2 mb-0">{{ $t('routes.guidedRecording.audioResult.statusHint') }}</p>

      <div v-if="hasActions" class="guided-audio-result-panel__actions mt-4">
        <v-btn
          v-if="canRecordAgain"
          color="primary"
          :disabled="isActing"
          :loading="isActing && activeAction === 'record-again'"
          data-cy="guidedRecording-audioResult-recordAgain"
          @click="$emit('record-again')"
        >
          <v-icon start aria-hidden="true">mdi-video-plus-outline</v-icon>
          {{ $t('routes.guidedRecording.audioResult.actionRecordAgain') }}
        </v-btn>
        <v-btn
          v-if="canContinueAnyway"
          color="warning"
          variant="outlined"
          :disabled="isActing"
          :loading="isActing && activeAction === 'warning-override'"
          data-cy="guidedRecording-audioResult-continueAnyway"
          @click="$emit('continue-anyway')"
        >
          <v-icon start aria-hidden="true">mdi-arrow-right-circle-outline</v-icon>
          {{ $t('routes.guidedRecording.audioResult.actionContinueAnyway') }}
        </v-btn>
        <v-btn
          v-if="canContinue"
          color="primary"
          variant="outlined"
          :disabled="isActing"
          :loading="isActing && activeAction === 'unavailable-continue'"
          data-cy="guidedRecording-audioResult-continue"
          @click="$emit('continue')"
        >
          <v-icon start aria-hidden="true">mdi-arrow-right-circle-outline</v-icon>
          {{ $t('routes.guidedRecording.audioResult.actionContinue') }}
        </v-btn>
        <v-btn
          v-if="canRetryProjection"
          color="primary"
          variant="outlined"
          :disabled="isActing"
          :loading="isActing && activeAction === 'projection-retry'"
          data-cy="guidedRecording-audioResult-retry"
          @click="$emit('retry')"
        >
          <v-icon start aria-hidden="true">mdi-refresh</v-icon>
          {{ $t('routes.guidedRecording.audioResult.actionRetry') }}
        </v-btn>
      </div>
    </v-alert>
  </section>
</template>

<script>
const AUDIO_STATUS_DETAILS = Object.freeze({
  fail: Object.freeze({ alertType: 'error', icon: 'mdi-alert-circle-outline', key: 'Failed' }),
  pass: Object.freeze({ alertType: 'success', icon: 'mdi-check-circle-outline', key: 'Pass' }),
  pending: Object.freeze({ alertType: 'info', icon: 'mdi-progress-clock', key: 'Pending' }),
  'projection-failure': Object.freeze({
    alertType: 'error',
    icon: 'mdi-alert-circle-outline',
    key: 'ProjectionFailure',
  }),
  unavailable: Object.freeze({ alertType: 'warning', icon: 'mdi-help-circle-outline', key: 'Unavailable' }),
  warning: Object.freeze({ alertType: 'warning', icon: 'mdi-alert-outline', key: 'Warning' }),
});

export default {
  name: 'GuidedAudioResultPanel',
  emits: ['continue', 'continue-anyway', 'record-again', 'retry'],
  props: {
    activeAction: { type: String, default: '' },
    isActing: { type: Boolean, default: false },
    isResolved: { type: Boolean, default: false },
    questionNumber: { type: Number, required: true },
    questionTotal: { type: Number, required: true },
    status: {
      type: String,
      required: true,
      validator: value => Object.hasOwn(AUDIO_STATUS_DETAILS, value),
    },
  },
  data() {
    return {
      announcement: '',
      headingId: 'guided-recording-audio-result-heading',
      previousStatus: '',
    };
  },
  computed: {
    alertType() {
      return AUDIO_STATUS_DETAILS[this.status].alertType;
    },
    canContinue() {
      return this.status === 'unavailable' && !this.isResolved;
    },
    canContinueAnyway() {
      return this.status === 'warning' && !this.isResolved;
    },
    canRecordAgain() {
      return ['fail', 'unavailable', 'warning'].includes(this.status) && !this.isResolved;
    },
    canRetryProjection() {
      return this.status === 'projection-failure';
    },
    guidance() {
      return this.$t(`routes.guidedRecording.audioResult.guidance${AUDIO_STATUS_DETAILS[this.status].key}`);
    },
    hasActions() {
      return this.canRecordAgain || this.canContinueAnyway || this.canContinue || this.canRetryProjection;
    },
    statusIcon() {
      return AUDIO_STATUS_DETAILS[this.status].icon;
    },
    statusText() {
      return this.$t(`routes.guidedRecording.audioResult.status${AUDIO_STATUS_DETAILS[this.status].key}`);
    },
  },
  watch: {
    status: {
      handler(status) {
        const announcementKey = this.previousStatus
          ? 'announcementStatusChanged'
          : 'announcementResultArrived';
        this.announcement = this.$t(`routes.guidedRecording.audioResult.${announcementKey}`, {
          current: this.questionNumber,
          status: this.statusText,
          total: this.questionTotal,
        });
        this.previousStatus = status;
        this.focusPanel();
      },
      immediate: true,
    },
  },
  methods: {
    focusPanel() {
      this.$nextTick(() => {
        this.$refs.panel?.focus();
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@layer app {
  .guided-audio-result-panel:focus {
    outline: 2px solid rgb(var(--v-theme-primary));
    outline-offset: 4px;
  }

  .guided-audio-result-panel__announcement {
    height: 1px;
    margin: -1px;
    overflow: hidden;
    position: absolute;
    width: 1px;
    clip: rect(0, 0, 0, 0);
  }

  .guided-audio-result-panel__heading,
  .guided-audio-result-panel__actions {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
  }

  .guided-audio-result-panel__heading {
    align-items: flex-start;
  }

  .guided-audio-result-panel__heading h3 {
    line-height: 1.25;
  }

  .guided-audio-result-panel__actions .v-btn:focus-visible {
    outline: 2px solid rgb(var(--v-theme-primary));
    outline-offset: 3px;
  }

  @media (max-width: 600px) {
    .guided-audio-result-panel__actions {
      align-items: stretch;
      flex-direction: column;
    }
  }
}
</style>
